import * as THREE from 'three';
import { addObject3DToParent } from '../../../utils/scene-object/addObject3DToParent';
import type { ImportedModelInstancedMeshUnit } from '../mesh-unit';
import { ImportedModelInstancedSelectionOverlayMesh } from './ImportedModelInstancedSelectionOverlayMesh';

/**
 * imported-model 实例选中覆盖对象管理器。
 *
 * 设计说明：
 * 1. 业务选择始终保存 occurrence 的语义对象或 source Mesh，本类只维护派生单槽覆盖对象；
 * 2. 特征树 Body 选中、线性阵列源零部件选中复用同一套创建、增量同步与释放规则；
 * 3. 正式 InstancedMesh 的 geometry、材质和实例矩阵所有权保持不变，覆盖对象只拥有自身 GPU 状态。
 */
export class ImportedModelInstancedSelectionOverlayManager {
  /** source Mesh UUID 到当前单槽覆盖对象的索引。 */
  private readonly overlayBySourceMeshId = new Map<string, ImportedModelInstancedSelectionOverlayMesh>();

  /**
   * 创建实例选中覆盖对象管理器。
   *
   * @param resolveInstancedMeshUnit 解析 source Mesh 当前所属的正式实例 Unit。
   * @param getSelectionMaterial 获取当前选择场景使用的共享覆盖材质。
   * @param resolveOverlayParent 选择层分组入口；命令调用默认保持原父级。
   */
  public constructor(
    private readonly resolveInstancedMeshUnit: (
      sourceMesh: THREE.Mesh,
    ) => ImportedModelInstancedMeshUnit | null,
    private readonly getSelectionMaterial: () => THREE.Material,
    private readonly resolveOverlayParent: (parent: THREE.Object3D) => THREE.Object3D = (parent) => parent,
  ) {}

  /**
   * 应用当前帧需要高亮的 source Mesh 集合。
   *
   * @param sourceMeshes 当前业务选择解析出的 source Mesh。
   * @param primitiveIndices 各 source 的精确 group 子集；省略表示完整零部件。
   */
  public apply(
    sourceMeshes: readonly THREE.Mesh[],
    primitiveIndices?: ReadonlyMap<THREE.Mesh, readonly number[] | undefined>,
  ): void {
    const nextOverlays = new Map<string, ImportedModelInstancedSelectionOverlayMesh>();

    try {
      sourceMeshes.forEach((sourceMesh) => {
        if (nextOverlays.has(sourceMesh.uuid)) {
          return;
        }
        const unit = this.resolveInstancedMeshUnit(sourceMesh);
        if (!unit) {
          return;
        }
        nextOverlays.set(
          sourceMesh.uuid,
          this.ensureOverlay(unit, sourceMesh, primitiveIndices?.get(sourceMesh)),
        );
      });
    } catch (error) {
      // 发布中途失败时归还本轮新建对象；原缓存交给调用方既有取消/重置边界处理。
      nextOverlays.forEach((overlay, id) => {
        if (this.overlayBySourceMeshId.get(id) !== overlay) overlay.disposeOverlay();
      });
      throw error;
    }

    // 本轮不再选中的 occurrence 必须立即释放独占 instanceMatrix 与 GPU 对象。
    this.overlayBySourceMeshId.forEach((overlay, sourceMeshId) => {
      if (!nextOverlays.has(sourceMeshId)) {
        overlay.disposeOverlay();
      }
    });
    this.overlayBySourceMeshId.clear();
    nextOverlays.forEach((overlay, sourceMeshId) => {
      this.overlayBySourceMeshId.set(sourceMeshId, overlay);
    });
  }

  /**
   * 清除并释放全部实例选中覆盖对象。
   */
  public reset(): void {
    this.overlayBySourceMeshId.forEach((overlay) => {
      overlay.disposeOverlay();
    });
    this.overlayBySourceMeshId.clear();
  }

  /**
   * 获取或创建一个 source Mesh 的单槽覆盖对象。
   *
   * @param unit source Mesh 当前所属的正式实例 Unit。
   * @param sourceMesh 当前 occurrence 的原始显示 Mesh。
   * @param primitiveIndices 需要绘制的局部 group 子集。
   * @returns 可复用的单槽覆盖对象。
   */
  private ensureOverlay(
    unit: ImportedModelInstancedMeshUnit,
    sourceMesh: THREE.Mesh,
    primitiveIndices?: readonly number[],
  ): ImportedModelInstancedSelectionOverlayMesh {
    const sourceParent = unit.object.parent;
    if (!sourceParent) {
      throw new Error(`实例选中覆盖对象缺少正式投影父级：${sourceMesh.uuid}`);
    }
    const overlayParent = this.resolveOverlayParent(sourceParent);

    const cachedOverlay = this.overlayBySourceMeshId.get(sourceMesh.uuid);
    if (cachedOverlay) {
      cachedOverlay.syncFromSourceUnit(unit, sourceMesh, primitiveIndices);
      if (cachedOverlay.parent !== overlayParent) {
        addObject3DToParent(cachedOverlay, overlayParent);
      }
      return cachedOverlay;
    }

    const overlay = new ImportedModelInstancedSelectionOverlayMesh(
      unit,
      sourceMesh,
      this.getSelectionMaterial(),
      primitiveIndices,
    );
    addObject3DToParent(overlay, overlayParent);
    return overlay;
  }
}
